import { z } from 'zod';

/** mirrors 03-database-spec.md §4.13 inbound_lots + §4.14 inbound_lines */

export const inboundLotStatusValues = ['imported', 'confirmed', 'locked'] as const;
export type InboundLotStatus = (typeof inboundLotStatusValues)[number];

export const inboundLotSchema = z.object({
  inbound_lot_id: z.string(),
  monthly_batch_id: z.string(),
  lot_code: z.string(),
  inbound_date: z.string(),
  source_company_id: z.string().nullable().optional(),
  vehicle: z.string().nullable().optional(),
  total_kg: z.string(),
  status: z.enum(inboundLotStatusValues),
  linked_dispatch_request_id: z.string().nullable().optional(),
});

export type InboundLotDto = z.infer<typeof inboundLotSchema>;

export const inboundLineSchema = z.object({
  inbound_line_id: z.string(),
  inbound_lot_id: z.string(),
  incoming_item_id: z.string().nullable().optional(),
  raw_incoming_item_name: z.string(),
  package_style: z.string().nullable().optional(),
  kg: z.string(),
  notes: z.string().nullable().optional(),
});

export type InboundLineDto = z.infer<typeof inboundLineSchema>;

export const inboundLotWithLinesSchema = inboundLotSchema.extend({
  lines: z.array(inboundLineSchema),
});

export type InboundLotWithLinesDto = z.infer<typeof inboundLotWithLinesSchema>;

/** Body for POST /api/monthly-batches/:id/inbound-lots. */
export const inboundLineCreateSchema = z.object({
  incoming_item_id: z.string().min(1, '入荷品名を選択してください'),
  kg: z
    .string()
    .min(1, 'kgを入力してください')
    .regex(/^\d+(\.\d{1,3})?$/, '0以上の数値（小数点3桁まで）を入力してください'),
  notes: z.string().optional(),
});

export const inboundLotCreateSchema = z.object({
  lot_code: z.string().min(1, 'ロットコードを入力してください'),
  inbound_date: z.string().min(1, '入荷日を入力してください'),
  source_company_id: z.string().min(1, '仕入先IDを入力してください'),
  package_style: z.string().optional(),
  lines: z.array(inboundLineCreateSchema).min(1, '明細を1件以上追加してください'),
});

export type InboundLotCreateInput = z.infer<typeof inboundLotCreateSchema>;

/**
 * Per-package inbound flow.
 *
 * The lot is created with only the truck panorama photos and a minimal
 * `{incoming_item_id, raw_incoming_item_name, notes?}` per package. Weights,
 * warehouse, photos are filled in later, package-by-package, via
 * POST /api/inbound-packages/:id/complete.
 */
// Operators often receive master tare values from the API as fixed-scale
// decimals such as 2.000. Keep the UI permissive up to 3 decimals while the
// backend still stores authoritative Decimal values.
const kgRegex = /^\d+(\.\d{1,3})?$/;

export const inboundPackageStatusValues = ['pending', 'completed', 'deleted'] as const;
export type InboundPackageStatus = (typeof inboundPackageStatusValues)[number];
export const requiredTruckPhotoKindValues = ['left', 'right', 'rear'] as const;
export type RequiredTruckPhotoKind = (typeof requiredTruckPhotoKindValues)[number];
export const truckPhotoKindValues = [...requiredTruckPhotoKindValues, 'other'] as const;
export type TruckPhotoKind = (typeof truckPhotoKindValues)[number];

/**
 * One row from `inbound_package_revisions`, joined with the actor's
 * `display_name`. Newest first per package.
 */
export const packageRevisionSchema = z.object({
  revision_id: z.string(),
  changed_at: z.string(),
  changed_by: z.string(),
  changed_by_name: z.string(),
  change_reason: z.string(),
  change_summary: z
    .object({
      is_initial_completion: z.boolean().optional(),
      changed_fields: z.array(z.string()).optional(),
      diff: z.record(z.object({ before: z.any(), after: z.any() })).optional(),
    })
    .passthrough(),
});

export type PackageRevisionDto = z.infer<typeof packageRevisionSchema>;

export const inboundPackageDtoSchema = z.object({
  inbound_package_id: z.string(),
  inbound_lot_id: z.string(),
  package_seq: z.number().int(),
  /** `{lot_code}_{seq:03}` — computed by the backend, displayed as a chip. */
  package_code: z.string(),
  incoming_item_id: z.string(),
  raw_incoming_item_name: z.string(),
  package_type_id: z.string().nullable().optional(),
  warehouse_id: z.string().nullable().optional(),
  gross_kg: z.string().nullable().optional(),
  tare_kg: z.string().nullable().optional(),
  net_kg: z.string().nullable().optional(),
  photo_evidence_file_ids: z.array(z.string()).default([]),
  status: z.enum(inboundPackageStatusValues),
  notes: z.string().nullable().optional(),
  /** Edit-history rows, newest first. */
  /**
   * Minted from a dismantling intermediate rather than received at the gate.
   * It borrows this lot only because `inbound_lot_id` is NOT NULL, so it must
   * never be summed into the lot's inbound totals.
   */
  derived_from_dismantling: z.boolean().default(false),
  revisions: z.array(packageRevisionSchema).default([]),
});

export type InboundPackageDto = z.infer<typeof inboundPackageDtoSchema>;

export const inboundLotWithPackagesDetailSchema = inboundLotSchema.extend({
  truck_photo_evidence_file_ids: z.array(z.string()).default([]),
  duplicate_truck_photo_evidence_file_ids: z.array(z.string()).default([]),
  /** FR-004 Part B — evidence_file_id → left/right/rear/other. */
  truck_photo_kinds: z.record(z.string()).default({}),
  packages: z.array(inboundPackageDtoSchema),
  measured_net_kg: z.string().nullable().optional(),
  declared_pallet_count: z.number().nullable().optional(),
  declared_fureikon_count: z.number().nullable().optional(),
  declared_cardboard_count: z.number().nullable().optional(),
  arrival_at: z.string().nullable().optional(),
  departure_at: z.string().nullable().optional(),
  /** Worksheet-level audit log, newest first (same shape as PackageRevisionDto). */
  revisions: z.array(packageRevisionSchema).default([]),
});

/**
 * Body for POST /api/inbound-lots/:id/worksheet — partial-update of
 * the worksheet metadata (vehicle + declared counts + measured net).
 * `change_reason` is always required server-side.
 */
export const inboundLotWorksheetUpdateSchema = z.object({
  vehicle: z.string().optional(),
  measured_net_kg: z.string().optional(),
  declared_pallet_count: z.number().int().nonnegative().optional(),
  declared_fureikon_count: z.number().int().nonnegative().optional(),
  declared_cardboard_count: z.number().int().nonnegative().optional(),
  arrival_at: z.string().optional(),
  departure_at: z.string().optional(),
  change_reason: z.string().min(1, '変更理由は必須です'),
});

export type InboundLotWorksheetUpdateInput = z.infer<typeof inboundLotWorksheetUpdateSchema>;

/**
 * Bulk warehouse assignment body. `warehouse_id == null` clears the
 * warehouse on every selected package. `change_reason` is required when
 * any selected package is already `completed` and its warehouse_id would
 * change (server enforces).
 */
export const bulkAssignWarehouseSchema = z.object({
  package_ids: z.array(z.string().min(1)).min(1),
  warehouse_id: z.string().nullable().optional(),
  change_reason: z.string().optional(),
});

export type BulkAssignWarehouseInput = z.infer<typeof bulkAssignWarehouseSchema>;

export type InboundLotWithPackagesDetailDto = z.infer<typeof inboundLotWithPackagesDetailSchema>;

export const createInboundLotWithPackagesResponseSchema = z.object({
  inbound_lot_id: z.string(),
  lot_code: z.string(),
  truck_photo_evidence_file_ids: z.array(z.string()).default([]),
  packages: z.array(inboundPackageDtoSchema),
});

export type CreateInboundLotWithPackagesResponse = z.infer<
  typeof createInboundLotWithPackagesResponseSchema
>;

/**
 * Quick-add per-package row sent on the create-with-packages endpoint.
 * Only enough to identify the item — weights/warehouse/photos are filled
 * in later via `/api/inbound-packages/:id/complete`.
 */
export const inboundPackageQuickAddSchema = z.object({
  incoming_item_id: z.string().min(1, '入荷品名を選択してください'),
  raw_incoming_item_name: z.string().min(1),
  notes: z.string().optional(),
});

export type InboundPackageQuickAddInput = z.infer<typeof inboundPackageQuickAddSchema>;

export const inboundLotWithPackagesCreateSchema = z.object({
  /**
   * Optional source alias for traceability. Server assigns the canonical code:
   * dispatch-linked lots inherit the dispatch trip code; no-dispatch lots use
   * YYYYMMDD_1, YYYYMMDD_2, ...
   */
  lot_code: z.string().optional(),
  /** Hidden source link when the lot is created from an accepted dispatch request. */
  dispatch_request_id: z.string().optional(),
  inbound_date: z.string().min(1, '入荷日を入力してください'),
  source_company_id: z.string().nullable().optional(),
  vehicle: z.string().nullable().optional(),
  notes: z.string().nullable().optional(),
  /**
   * Truck-arrival worksheet fields. The operator copies these from the
   * weighbridge / counting slip:
   *  - measured_net_kg (計量正味) — sanity-checked against Σnet at lot detail
   *  - declared_pallet_count (パレット数 P) — must equal #pallet packages
   *  - declared_fureikon_count (フレコン数 F) — must equal #bulk-bag packages
   *  - declared_cardboard_count (段ボール、プラダスト数)
   *
   * All four are sent as strings so the form library can handle empty input;
   * the backend coerces to Decimal / i32.
   */
  measured_net_kg: z.string().optional(),
  declared_pallet_count: z.string().optional(),
  declared_fureikon_count: z.string().optional(),
  declared_cardboard_count: z.string().optional(),
  /**
   * Vehicle arrival / departure timestamps. Held as ISO-8601 strings
   * (from a `datetime-local` input); the backend stores them verbatim in
   * `gross_context`.
   */
  arrival_at: z.string().optional(),
  departure_at: z.string().optional(),
  /**
   * Step 1 uploads one arrival photo/video before the draft lot is created.
   * Every linked truck photo must carry its shot direction so the final
   * confirm gate can require left + right + rear coverage instead of a raw
   * count.
   */
  truck_photo_evidence_file_ids: z.array(z.string().min(1)),
  truck_photo_kinds: z.record(z.enum(truckPhotoKindValues)),
  // Validation of "at least 1 package" lives in the UI (submit disabled
  // until rows exist) — the schema is just for the lot header fields here.
  packages: z.array(inboundPackageQuickAddSchema),
});

export type InboundLotWithPackagesInput = z.infer<typeof inboundLotWithPackagesCreateSchema>;

/**
 * Form model for the inbound lot create dialog. Uses the
 * repeatable-rows (useFieldArray) pattern: the operator adds one row per
 * incoming item with a quantity, and each row is expanded into `quantity`
 * skeleton package objects (one inbound_package per physical unit) before
 * submitting once to POST /inbound-lots/with-packages.
 *
 * `quantity` is kept as a string so the form library can hold empty input;
 * it is parsed to an integer on submit (the wire shape has no quantity —
 * the backend materialises N rows from N expanded package objects).
 */
export const inboundBulkPackageRowSchema = z.object({
  incoming_item_id: z.string().min(1, '入荷品名を選択してください'),
  raw_incoming_item_name: z.string().min(1),
  quantity: z
    .string()
    .min(1, '数量を入力してください')
    .regex(/^[1-9]\d*$/, '1以上の整数を入力してください'),
  notes: z.string().optional(),
});

export type InboundBulkPackageRowInput = z.infer<typeof inboundBulkPackageRowSchema>;

export const inboundBulkCreateSchema = z.object({
  inbound_date: z.string().min(1, '入荷日を入力してください'),
  source_company_id: z.string().nullable().optional(),
  vehicle: z.string().nullable().optional(),
  notes: z.string().nullable().optional(),
  rows: z.array(inboundBulkPackageRowSchema).min(1, 'パッケージを1件以上追加してください'),
});

export type InboundBulkCreateInput = z.infer<typeof inboundBulkCreateSchema>;

/**
 * Body for POST /api/inbound-packages/:id/complete — fills in the
 * per-package details (warehouse, weights, photos) once the operator
 * has weighed the package.
 */
export const inboundPackageCompleteSchema = z.object({
  incoming_item_id: z.string().min(1, '入荷品名を選択してください'),
  package_type_id: z.string().min(1, '包装種類を選択してください'),
  warehouse_id: z.string().min(1, '倉庫を選択してください'),
  gross_kg: z
    .string()
    .min(1, '総重を入力してください')
    .regex(kgRegex, '0以上の数値（小数点3桁まで）を入力してください'),
  tare_kg: z
    .string()
    .min(1, '風袋を入力してください')
    .regex(kgRegex, '0以上の数値（小数点3桁まで）を入力してください'),
  photo_evidence_file_ids: z.array(z.string().min(1)).min(1, '写真は1枚以上必要です'),
  notes: z.string().optional(),
  /** Audit reason — required when re-editing an already-completed package. */
  change_reason: z.string().optional(),
});

export type InboundPackageCompleteInput = z.infer<typeof inboundPackageCompleteSchema>;

export const inboundPackageDeleteSchema = z.object({
  reason: z.string().trim().min(1, '取消理由を入力してください').max(2000),
});

export type InboundPackageDeleteInput = z.infer<typeof inboundPackageDeleteSchema>;

export interface InboundDeleteBlocker {
  code: string;
  count: number;
}

export interface InboundDeletePreview {
  inbound_lot_id: string;
  linked_dispatch_request_id?: string | null;
  purge_after: string;
  inbound_lot_count: number;
  inbound_package_count: number;
  dismantling_session_count: number;
  dismantling_output_count: number;
  inventory_movement_count: number;
  return_step: 'dispatch' | 'manual_creation' | string;
  can_cancel: boolean;
  can_delete: boolean;
  blocker_count: number;
  blockers: InboundDeleteBlocker[];
  impact_counts?: Record<string, number>;
}

export interface InboundDeleteResult {
  inbound_lot_id: string;
  deleted_at: string;
  purge_after: string;
  archive_id: string;
  impact_counts?: Record<string, number>;
}

export interface DeletedInboundLot {
  archive_id: string;
  inbound_lot_id: string;
  monthly_batch_id: string;
  platform_operator_id?: string | null;
  source_company_id: string;
  lot_code?: string | null;
  deleted_at: string;
  deleted_by: string;
  deleted_by_display_name: string;
  deleted_by_email: string;
  delete_reason: string;
  superseded_at?: string | null;
  replacement_inbound_lot_id?: string | null;
  purge_after: string;
  return_step: 'dispatch' | 'manual_creation' | string;
  linked_dispatch_request_id?: string | null;
  recoverable: boolean;
  days_remaining: number;
  purged_at?: string | null;
  restored_at?: string | null;
  impact_counts?: Record<string, number>;
}
